/**
 * Ключевые показатели территории — вертикальный стек карточек (§3 FE-15).
 *
 * Принимает данные через props (fetchит родитель — ContextSidebar).
 * Форматирование чисел через Intl.NumberFormat('ru-RU').
 * Карточки видны всегда: показатель без значения (null / нет данных по КАТО)
 * рендерится названием и иконкой — набор карточек стабилен при смене КАТО.
 *
 * Каждый показатель — отдельный виджет витрины (`left-menu.*`, US-09 FR-1):
 * выключенный супер-администратором не рендерится вовсе, остальные
 * смыкаются. Список идентификаторов — `src/consts/modules.ts`.
 */
import {
  ArrowLeftRight,
  Building2,
  MapPin,
  UserSearch,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Indicator } from './Indicator';
import { ModuleGate } from '../../common/ModuleGate';
import { MODULES } from '../../../consts/modules';
import type { GeneralInfo } from '../../../types/public';

/** Ключи показателей = поля GeneralInfo, по которым есть карточки. */
export type IndicatorTrends = Partial<
  Record<
    | 'population'
    | 'village_count'
    | 'saldo_migration'
    | 'avg_salary'
    | 'unemployed'
    | 'area_km2',
    number | null
  >
>;

interface IndicatorsProps {
  general?: GeneralInfo;
  /** Чипы динамики (API их пока не отдаёт — приходят из hardData-мока). */
  trends?: IndicatorTrends;
}

const fmt = (v: number | null | undefined): string | null =>
  v != null ? Intl.NumberFormat('ru-RU').format(v) : null;

/** Территория: не больше одного знака после запятой (замечание ревью). */
const fmt1 = (v: number | null | undefined): string | null =>
  v != null
    ? Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v)
    : null;

export const Indicators = ({ general, trends }: IndicatorsProps) => {
  const { t } = useTranslation();

  const saldo = general?.saldo_migration;

  // Чип тренда показывается только вместе со значением показателя.
  const trendOf = (
    key: keyof IndicatorTrends,
    value: number | null | undefined,
  ): number | null => (value != null ? (trends?.[key] ?? null) : null);

  const cards: Array<{
    key: keyof IndicatorTrends;
    module: string;
    icon: LucideIcon;
    label: string;
    value: string | null;
    unit: string;
    accent: 'green' | 'blue' | 'danger';
    raw: number | null | undefined;
  }> = [
    {
      key: 'population',
      module: MODULES.leftMenu.population,
      icon: Users,
      label: t('menu.population'),
      value: fmt(general?.population),
      unit: t('units.people'),
      accent: 'green',
      raw: general?.population,
    },
    {
      key: 'village_count',
      module: MODULES.leftMenu.villageCount,
      icon: Building2,
      label: t('menu.snpCount'),
      value: fmt(general?.village_count),
      unit: t('units.snp'),
      accent: 'blue',
      raw: general?.village_count,
    },
    {
      key: 'saldo_migration',
      module: MODULES.leftMenu.saldoMigration,
      icon: ArrowLeftRight,
      label: t('menu.saldoMigration'),
      value: fmt(saldo),
      unit: (saldo ?? 0) < 0 ? t('units.outflow') : t('units.inflow'),
      accent: (saldo ?? 0) < 0 ? 'danger' : 'green',
      raw: saldo,
    },
    {
      key: 'avg_salary',
      module: MODULES.leftMenu.avgSalary,
      icon: Wallet,
      label: t('menu.avgSalary'),
      value: fmt(general?.avg_salary),
      unit: t('units.tenge'),
      accent: 'green',
      raw: general?.avg_salary,
    },
    {
      key: 'unemployed',
      module: MODULES.leftMenu.unemployed,
      icon: UserSearch,
      label: t('menu.unemployed'),
      value: fmt(general?.unemployed),
      unit: t('units.people'),
      accent: 'green',
      raw: general?.unemployed,
    },
    {
      key: 'area_km2',
      module: MODULES.leftMenu.area,
      icon: MapPin,
      label: t('menu.area'),
      value: fmt1(general?.area_km2),
      unit: t('units.km2'),
      accent: 'blue',
      raw: general?.area_km2,
    },
  ];

  return (
    <div className="flex flex-col gap-3" data-testid="indicators-list">
      {cards.map((card) => (
        <ModuleGate key={card.key} id={card.module}>
          <Indicator
            icon={card.icon}
            label={card.label}
            value={card.value}
            unit={card.unit}
            accent={card.accent}
            trend={trendOf(card.key, card.raw)}
            testId={`indicator-${card.key}`}
          />
        </ModuleGate>
      ))}
    </div>
  );
};
